import styled from 'styled-components/macro'

import { Label, Layout, TextField } from '@loadsmart/loadsmart-ui'

import { calculateFlatRate, calculateRatePerDistance } from 'core/domain/LaneRate'
import { formatRate, formatRatePerDistance } from 'core/presenters/laneRatePresenter'
import { useInput } from 'core/ui/hooks/useInput'
import { getNumberFromString } from 'core/utils/number'

const StyledLayoutGroup = styled(Layout.Group)`
  flex-wrap: nowrap;
`

// use attrs due to Miranda applying style
// to input wrapper instead of input component
const StyledTextField = styled(TextField).attrs({ style: { width: '100%' } })``

export type QuoteRateInputFieldsProps = {
  disabled?: boolean
  initialFlatRateValue?: number
  initialRatePerMileValue?: number
  mileage?: number
  onRateChange: (flatRate: number) => void
}

export function QuoteRateInputFields({
  disabled = false,
  mileage,
  onRateChange,
}: QuoteRateInputFieldsProps) {
  const { value: flatRateValue, setValue: setFlatRateValue } = useInput()
  const { value: ratePerMileValue, setValue: setRatePerMileValue } = useInput()

  const hasMileage = Boolean(mileage)
  const disablePerMileRate = disabled || !hasMileage

  function handleRateChange(event: React.FocusEvent<HTMLInputElement>) {
    const {
      target: { name, value },
    } = event
    const rateValue = getNumberFromString(value)

    if (name === 'flat-rate') {
      setFlatRateValue(rateValue)

      // calculate new rate per mile
      const calculatedRatePerMile = calculateRatePerDistance(rateValue, mileage!)

      setRatePerMileValue(calculatedRatePerMile)

      onRateChange(rateValue)
    } else {
      // format rate per mile
      const ratePerMile = formatRatePerDistance(rateValue)

      setRatePerMileValue(ratePerMile)

      // calculate new flat rate
      const calculatedFlatRate = calculateFlatRate(ratePerMile, mileage!)

      setFlatRateValue(calculatedFlatRate)

      onRateChange(calculatedFlatRate)
    }
  }

  return (
    <StyledLayoutGroup space="s">
      <Layout.Stack space="xs">
        <Label htmlFor="flat-rate">Flat rate</Label>
        <StyledTextField
          id="flat-rate"
          name="flat-rate"
          data-testid="flat-rate"
          placeholder="$"
          onChange={handleRateChange}
          value={formatRate(flatRateValue, false)}
          maxLength={9}
          disabled={disabled}
        />
      </Layout.Stack>
      {hasMileage && (
        <Layout.Stack space="xs">
          <Label htmlFor="rate-per-mile">Rate per mile</Label>
          <StyledTextField
            id="rate-per-mile"
            name="rate-per-mile"
            data-testid="rate-per-mile"
            placeholder="$"
            value={formatRate(ratePerMileValue, true)}
            onChange={handleRateChange}
            disabled={disablePerMileRate}
          />
        </Layout.Stack>
      )}
    </StyledLayoutGroup>
  )
}
